import React, { useEffect, useState, useMemo } from "react";

import { Grid, MenuItem, SelectChangeEvent } from "@mui/material";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { Instruction } from "../../../../../../../models/instruction";
import { Status, InstructionWhen } from "../../../../../../../util/enums";
import Input from "../../../../../../../components/Form/Input";
import Select from "../../../../../../../components/Form/Select";
import Modal from "../../../../../../../components/Generic/Modal";
import useStyles from "./styles";

interface Props {
  instruction?: Instruction;
  hasError?: boolean;
  open: boolean;
  isLoading?: boolean;
  closeModal: () => void;
  submit: (instruction: Instruction) => void;
}

const InstructionModal = ({
  instruction,
  hasError,
  open,
  isLoading,
  closeModal,
  submit,
}: Props) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();

  const [selectedInstruction, setSelectedInstruction] = useState<Instruction>();
  const [status, setStatus] = useState<string>(Status.INACTIVE);

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);

  const whenOptions = useMemo(
    () => [
      { value: InstructionWhen.DELIVERY, label: InstructionWhen.DELIVERY },
      { value: InstructionWhen.UPLIFT, label: InstructionWhen.UPLIFT },
      { value: InstructionWhen.COORDINATOR, label: InstructionWhen.COORDINATOR },
    ],
    []
  );

  const getErrorByField = (field: string) => errors?.find((error) => error.path === field)?.message;

  const schema = yup.object().shape({
    code: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    description: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    when: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
  });

  const clearFields = () => {
    setSelectedInstruction(undefined);
    setStatus(Status.INACTIVE);
  };

  const handleValidateSuccess = (instruction?: Instruction): boolean => {
    schema.validateSync(
      {
        code: instruction?.code,
        description: instruction?.description,
        when: instruction?.when,
      },
      { abortEarly: false }
    );

    setErrors([]);

    return true;
  };

  const handleValidateError = (error: any): boolean => {
    if (error instanceof yup.ValidationError) setErrors(error?.inner);

    return false;
  };

  const isValid = (instruction?: Instruction) => {
    try {
      return handleValidateSuccess(instruction);
    } catch (error) {
      return handleValidateError(error);
    }
  };

  const handleSubmit = () => {
    if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);

    if (isValid(selectedInstruction)) {
      if (selectedInstruction) submit({ ...selectedInstruction, status });
    }
  };

  useEffect(() => {
    if (instruction) {
      setSelectedInstruction(instruction);
      setStatus(instruction.status || "");
    }
  }, [instruction]);

  useEffect(() => {
    if (hasError !== undefined && !hasError) {
      clearFields();
      closeModal();
    }
  }, [hasError]);

  useEffect(() => {
    if (hasFormBeenSubmitted) {
      isValid(selectedInstruction);
    }
  }, [selectedInstruction, hasFormBeenSubmitted]);

  return (
    <Modal
      open={open}
      handleClose={closeModal}
      title={instruction ? translate("editInstruction") : translate("createInstruction")}
      data-cy="instructionModal"
      showActionButtons
      actionButtonProps={{
        dataCyCancel: "cancelButton",
        onCancel: () => {
          clearFields();
          closeModal();
        },
        textSave: instruction ? translate("update") : translate("create"),
        loading: isLoading,
        saveDisabled: isLoading,
        dataCySave: "submitButton",
        onSave: handleSubmit,
      }}
      labelStatus={translate("status")}
      showStatusField={true}
      statusFieldProps={{
        label: translate("instructionStatus"),
        value: status,
        dataCy: "selectStatus",
        placeholder: translate("selectAnOption"),
        handleChange: (value) => setStatus(value),
      }}
    >
      <section className={classes.modalSection}>
        <Grid container direction="row" alignItems="flex-start" spacing={2}>
          <Grid item md={6} lg={6}>
            <Input
              name="code"
              label={translate("code")}
              placeholder={translate("code")}
              error={!!getErrorByField("code")}
              errorMessage={getErrorByField("code")}
              value={selectedInstruction?.code}
              onChange={(event) =>
                setSelectedInstruction((state) =>
                  state
                    ? { ...state, code: event.target.value }
                    : ({ code: event.target.value } as Instruction)
                )
              }
            />
          </Grid>
          <Grid item md={6} lg={6}>
            <Input
              name="description"
              label={translate("name")}
              placeholder={translate("name")}
              error={!!getErrorByField("description")}
              errorMessage={getErrorByField("description")}
              value={selectedInstruction?.description}
              onChange={(event) =>
                setSelectedInstruction((state) =>
                  state
                    ? { ...state, description: event.target.value }
                    : ({ description: event.target.value } as Instruction)
                )
              }
            />
          </Grid>
          <Grid item md={12} lg={12}>
            <Select
              data-cy="whenSelect"
              name="when"
              label={translate("when")}
              value={selectedInstruction?.when}
              error={!!getErrorByField("when")}
              errorMessage={getErrorByField("when")}
              onChange={(event: SelectChangeEvent<any>) =>
                setSelectedInstruction((state) =>
                  state
                    ? { ...state, when: event.target.value }
                    : ({ when: event.target.value } as Instruction)
                )
              }
              displayEmpty
              placeholder={translate("selectAnOption")}
            >
              {whenOptions?.map((option) => (
                <MenuItem
                  data-cy={option.value.toLowerCase()}
                  value={option.value}
                  key={option.value}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>
      </section>
    </Modal>
  );
};

export default InstructionModal;
